import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Lock, CheckCircle, Clock, Bell, CreditCard,
  AlertTriangle, ArrowUpRight, ArrowDownLeft, Flag, Ban, UserCheck,
  DollarSign, TrendingUp, Activity, FileText, XCircle, Search,
  Shield, Zap, Download, UserPlus
} from 'lucide-react';
import { PayoutsTab } from './AdminPayouts';
import { FraudTab } from './AdminFraud';
import AdminGroups from './AdminGroups';
import { AutoRunHistory } from '@/components/AutoRunHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const h = getAuthHeaders();
      const [sRes, aRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers: h }),
        axios.get(`${API_URL}/api/payout/alerts`, { headers: h }).catch(() => ({ data: { ready_count: 0, pending_count: 0, at_risk_count: 0 } })),
      ]);
      setStats(sRes.data);
      setAlerts(aRes.data);
    } catch { toast.error('Failed'); navigate('/'); }
    finally { setLoading(false); }
  };

  const fmtCur = (a) => '\u20a6' + new Intl.NumberFormat('en-NG').format(a || 0);

  if (loading) {
    return (<div className="min-h-screen bg-stone-50 p-4"><div className="max-w-6xl mx-auto">
      <Skeleton className="h-8 w-48 mb-6" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
    </div></div>);
  }

  const readyCount = alerts ? alerts.ready_count : 0;
  const riskCount = alerts ? alerts.at_risk_count : 0;
  const pendAlertCount = alerts ? alerts.pending_count : 0;
  const pendDeposits = stats ? stats.pending_deposits : 0;

  return (
    <div className="min-h-screen bg-stone-50 pb-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/profile')} className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow flex-shrink-0" data-testid="admin-back-button">
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-[#0B1F3A]">Admin Control Center</h1>
            <p className="text-xs md:text-sm text-slate-500">Payouts, risk, defaults & fraud monitoring</p>
          </div>
        </div>

        {(riskCount > 0 || readyCount > 0) && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-alert-bar">
            {riskCount > 0 && <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 font-medium"><AlertTriangle size={14}/>{riskCount} at risk</div>}
            {readyCount > 0 && <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 font-medium"><CheckCircle size={14}/>{readyCount} ready for payout</div>}
            {pendAlertCount > 0 && <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700 font-medium"><Clock size={14}/>{pendAlertCount} pending</div>}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard icon={<Users size={18}/>} color="#0B1F3A" label="Users" value={stats ? stats.total_users : 0} />
          <StatCard icon={<DollarSign size={18}/>} color="#2272C5" label="Available" value={fmtCur(stats ? stats.total_available_funds : 0)} isText />
          <StatCard icon={<Lock size={18}/>} color="#d97706" label="Locked" value={fmtCur(stats ? stats.total_locked_funds : 0)} isText />
          <StatCard icon={<Clock size={18}/>} color="#dc2626" label="Pending Deposits" value={pendDeposits} highlight={pendDeposits > 0} />
        </div>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
          <MiniStat label="Active Groups" value={stats ? stats.active_groups : 0} />
          <MiniStat label="Verified" value={stats ? stats.verified_users : 0} />
          <MiniStat label="Flagged" value={stats ? stats.flagged_users : 0} warn />
          <MiniStat label="Frozen" value={stats ? stats.frozen_users : 0} warn />
          <MiniStat label="Transactions" value={stats ? stats.total_transactions : 0} />
          <MiniStat label="Completed" value={stats ? stats.completed_groups : 0} />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 mb-4">
            <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-10 h-auto gap-1">
              <TabsTrigger value="overview" className="text-[10px] py-2 px-3 whitespace-nowrap">Overview</TabsTrigger>
              <TabsTrigger value="payouts" className="text-[10px] py-2 px-3 relative whitespace-nowrap">Payouts{readyCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-[9px] rounded-full flex items-center justify-center">{readyCount}</span>}</TabsTrigger>
              <TabsTrigger value="deposits" className="text-[10px] py-2 px-3 relative whitespace-nowrap">Deposits{pendDeposits > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center">{pendDeposits}</span>}</TabsTrigger>
              <TabsTrigger value="transactions" className="text-[10px] py-2 px-3 whitespace-nowrap">Transactions</TabsTrigger>
              <TabsTrigger value="kyc" className="text-[10px] py-2 px-3 whitespace-nowrap">KYC</TabsTrigger>
              <TabsTrigger value="users" className="text-[10px] py-2 px-3 whitespace-nowrap">Users</TabsTrigger>
              <TabsTrigger value="fraud" className="text-[10px] py-2 px-3 whitespace-nowrap">Fraud</TabsTrigger>
              <TabsTrigger value="groups" className="text-[10px] py-2 px-3 whitespace-nowrap">Groups</TabsTrigger>
              <TabsTrigger value="scheduler" className="text-[10px] py-2 px-3 whitespace-nowrap">Scheduler</TabsTrigger>
              <TabsTrigger value="rewards" className="text-[10px] py-2 px-3 whitespace-nowrap">Rewards</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview"><OverviewTab stats={stats} alerts={alerts} fmtCur={fmtCur} headers={getAuthHeaders()} /></TabsContent>
          <TabsContent value="payouts"><PayoutsTab headers={getAuthHeaders()} onUpdate={fetchData} /></TabsContent>
          <TabsContent value="deposits"><DepositsTab headers={getAuthHeaders()} onUpdate={fetchData} /></TabsContent>
          <TabsContent value="transactions"><TransactionsTab headers={getAuthHeaders()} /></TabsContent>
          <TabsContent value="kyc"><KYCTab headers={getAuthHeaders()} /></TabsContent>
          <TabsContent value="users"><UsersTab headers={getAuthHeaders()} fmtCur={fmtCur} onUpdate={fetchData} /></TabsContent>
          <TabsContent value="fraud"><FraudTab headers={getAuthHeaders()} /></TabsContent>
          <TabsContent value="groups"><AdminGroups headers={getAuthHeaders()} onUpdate={fetchData} /></TabsContent>
          <TabsContent value="scheduler"><SchedulerTab headers={getAuthHeaders()} onUpdate={fetchData} /></TabsContent>
          <TabsContent value="rewards"><AdminRewardsTab headers={getAuthHeaders()} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const StatCard = ({ icon, color, label, value, isText, highlight }) => (
  <Card className={`glass-card ${highlight ? 'ring-2 ring-red-400' : ''}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-2"><div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}><span style={{ color }}>{icon}</span></div></div>
      <p className={`${isText ? 'text-base' : 'text-2xl'} font-bold text-[#0B1F3A]`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </CardContent>
  </Card>
);

const MiniStat = ({ label, value, warn }) => (
  <div className={`p-3 rounded-xl text-center ${warn && value > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-slate-200'}`}>
    <p className={`text-lg font-bold ${warn && value > 0 ? 'text-red-600' : 'text-[#0B1F3A]'}`}>{value || 0}</p>
    <p className="text-[10px] text-slate-500">{label}</p>
  </div>
);

/* ─── OVERVIEW TAB ─── */
const OverviewTab = ({ stats, alerts, fmtCur, headers }) => {
  const [recentTx, setRecentTx] = useState([]);
  const [payoutLogs, setPayoutLogs] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/transactions?limit=5`, { headers }).then(r => setRecentTx(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/payout-logs`, { headers }).then(r => setPayoutLogs(r.data.slice(0, 5))).catch(() => {});
    axios.get(`${API_URL}/api/admin/recent-signups`, { headers }).then(r => setRecentSignups(r.data)).catch(() => {});
  }, []);

  const totalAvail = stats ? stats.total_available_funds : 0;
  const totalLocked = stats ? stats.total_locked_funds : 0;
  const totalApproved = stats ? stats.total_deposits_approved : 0;
  const alertReady = alerts ? alerts.ready_count : 0;
  const alertPending = alerts ? alerts.pending_count : 0;
  const alertRisk = alerts ? alerts.at_risk_count : 0;
  const pwaInstalls = stats ? stats.pwa_installs : 0;

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-4">
      {/* App Downloads */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-[#2272C5]/10">
                <Download size={20} className="text-[#2272C5]" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">App Downloads</p>
                <p className="text-2xl font-bold text-[#0B1F3A]">{pwaInstalls}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-slate-500">Total Users</p>
              <p className="text-2xl font-bold text-[#2272C5]">{stats ? stats.total_users : 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Members */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2">
            <UserPlus size={18} className="text-emerald-600"/>New Members
            <Badge className="bg-emerald-100 text-emerald-700 text-xs ml-auto">{recentSignups.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentSignups.length === 0 ? <p className="text-center py-4 text-slate-500 text-sm">No signups yet</p> :
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {recentSignups.map((u, i) => (
              <div key={u.id || i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50" data-testid={`recent-signup-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#0B1F3A] flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">{(u.full_name || '?').charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0B1F3A] truncate">{u.full_name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-slate-400">{timeAgo(u.created_at)}</p>
                  {u.is_verified ? 
                    <Badge className="bg-emerald-100 text-emerald-700 text-[9px] mt-0.5">Verified</Badge> :
                    <Badge className="bg-slate-100 text-slate-500 text-[9px] mt-0.5">Unverified</Badge>
                  }
                </div>
              </div>
            ))}
          </div>}
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><TrendingUp size={18} className="text-[#2272C5]"/>Financial Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200"><p className="text-xs text-emerald-700 font-medium">Available</p><p className="text-lg font-bold text-emerald-800">{fmtCur(totalAvail)}</p></div>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200"><p className="text-xs text-amber-700 font-medium">Locked</p><p className="text-lg font-bold text-amber-800">{fmtCur(totalLocked)}</p></div>
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200"><p className="text-xs text-blue-700 font-medium">Combined</p><p className="text-lg font-bold text-blue-800">{fmtCur(totalAvail + totalLocked)}</p></div>
            <div className="p-3 rounded-xl bg-purple-50 border border-purple-200"><p className="text-xs text-purple-700 font-medium">Approved Deposits</p><p className="text-lg font-bold text-purple-800">{totalApproved}</p></div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Zap size={18} className="text-amber-500"/>Payout Status</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center"><p className="text-2xl font-bold text-emerald-700">{alertReady}</p><p className="text-xs text-emerald-600">Ready</p></div>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-center"><p className="text-2xl font-bold text-amber-700">{alertPending}</p><p className="text-xs text-amber-600">Pending</p></div>
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-center"><p className="text-2xl font-bold text-red-700">{alertRisk}</p><p className="text-xs text-red-600">At Risk</p></div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Activity size={18} className="text-[#2272C5]"/>Recent Transactions</CardTitle></CardHeader>
        <CardContent>
          {recentTx.length === 0 ? <p className="text-center py-4 text-slate-500">No transactions</p> :
          <div className="space-y-2">{recentTx.map(tx => {
            const isCredit = ['credit','payout','security_release','qualification_release'].includes(tx.type);
            const txDesc = tx.description;
            const txUser = tx.user_name;
            const txDate = tx.created_at;
            const txAmt = tx.amount;
            return (
              <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <div className={`p-2 rounded-lg ${isCredit ? 'bg-blue-100' : 'bg-amber-100'}`}>
                  {isCredit ? <ArrowDownLeft size={14} className="text-blue-600"/> : <ArrowUpRight size={14} className="text-amber-600"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0B1F3A] truncate">{txDesc}</p>
                  <p className="text-xs text-slate-500">{txUser} &middot; {new Date(txDate).toLocaleDateString('en-NG', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}</p>
                </div>
                <p className={`text-sm font-bold ${isCredit ? 'text-blue-600' : 'text-amber-600'}`}>{isCredit ? '+' : '-'}{fmtCur(txAmt)}</p>
              </div>
            );
          })}</div>}
        </CardContent>
      </Card>

      {payoutLogs.length > 0 && (
        <Card className="glass-card">
          <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><FileText size={18} className="text-[#2272C5]"/>Payout Logs</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">{payoutLogs.map(log => {
              const logGroup = log.group_name;
              const logPos = log.position;
              const logRecipient = log.recipient_name;
              const logTrigger = log.trigger_type;
              const logDate = log.created_at;
              const logNet = log.net_payout;
              const logOverride = log.override_reason;
              return (
                <div key={log.id} className="p-3 rounded-xl bg-slate-50 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0B1F3A]">{logGroup} — Position {logPos}</p>
                    <p className="text-xs text-slate-500">{logRecipient} &middot; {logTrigger.replace('_',' ')} &middot; {new Date(logDate).toLocaleDateString('en-NG')}</p>
                    {logOverride && <p className="text-xs text-red-600 mt-1">Override: {logOverride}</p>}
                  </div>
                  <p className="text-sm font-bold text-emerald-600">{fmtCur(logNet)}</p>
                </div>
              );
            })}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── DEPOSITS TAB ─── */
const DepositsTab = ({ headers, onUpdate }) => {
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showReject, setShowReject] = useState(null);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try { setDeposits((await axios.get(`${API_URL}/api/admin/deposits?status=${filter}`, { headers })).data); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const approve = async (id) => { setActionId(id); try { await axios.put(`${API_URL}/api/admin/deposits/${id}/approve`, { admin_notes: 'Approved' }, { headers }); toast.success('Approved & credited'); fetchDeposits(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const reject = async (id) => { if (!rejectNotes.trim()) { toast.error('Reason required'); return; } setActionId(id); try { await axios.put(`${API_URL}/api/admin/deposits/${id}/reject`, { admin_notes: rejectNotes }, { headers }); toast.success('Rejected'); setShowReject(null); setRejectNotes(''); fetchDeposits(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><DollarSign size={18} className="text-[#2272C5]"/>Deposits</CardTitle>
        <div className="flex gap-2 mt-2">{['pending','approved','rejected','all'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} className={`text-xs rounded-lg ${filter === f ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'}`} onClick={() => setFilter(f)} data-testid={`deposit-filter-${f}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</Button>
        ))}</div>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-20 rounded-xl"/> :
        deposits.length === 0 ? <p className="text-center py-8 text-slate-500">No deposits</p> :
        <div className="space-y-3">{deposits.map(dep => {
          const depStatus = dep.status;
          const depName = dep.user_name;
          const depEmail = dep.user_email;
          const depAmt = dep.amount;
          const depRef = dep.reference;
          const depDate = dep.created_at;
          const depNotes = dep.admin_notes;
          const depReviewer = dep.reviewed_by_name;
          const depReviewDate = dep.reviewed_at;
          const bgClass = depStatus === 'pending' ? 'bg-amber-50 border-amber-200' : depStatus === 'approved' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';
          const badgeClass = depStatus === 'pending' ? 'bg-amber-100 text-amber-700' : depStatus === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700';
          return (
            <div key={dep.id} className={`p-4 rounded-xl border-2 ${bgClass}`} data-testid={`deposit-item-${dep.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div><p className="font-bold text-[#0B1F3A]">{depName}</p><p className="text-xs text-slate-500">{depEmail}</p></div>
                <div className="text-right"><p className="text-lg font-bold text-[#0B1F3A]">{'\u20a6'}{new Intl.NumberFormat('en-NG').format(depAmt)}</p><Badge className={badgeClass}>{depStatus}</Badge></div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-600 mb-2">
                <span><FileText size={12} className="inline mr-1"/>Ref: <span className="font-mono font-bold">{depRef}</span></span>
                <span><Clock size={12} className="inline mr-1"/>{new Date(depDate).toLocaleDateString('en-NG', {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
              </div>
              {depNotes && <p className="text-xs text-slate-600 bg-white/60 p-2 rounded-lg mb-2">Admin: {depNotes}</p>}
              {depReviewer && <p className="text-[10px] text-slate-400">By {depReviewer} on {new Date(depReviewDate).toLocaleDateString('en-NG')}</p>}
              {depStatus === 'pending' && (
                <div className="flex gap-2 mt-3">
                  {showReject === dep.id ? (
                    <div className="flex-1 space-y-2">
                      <input value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} placeholder="Reason..." className="w-full px-3 py-2 rounded-lg border border-red-300 text-sm" data-testid={`reject-reason-${dep.id}`}/>
                      <div className="flex gap-2">
                        <Button size="sm" variant="destructive" className="text-xs" onClick={() => reject(dep.id)} disabled={actionId === dep.id} data-testid={`confirm-reject-${dep.id}`}>Reject</Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowReject(null); setRejectNotes(''); }}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1" onClick={() => approve(dep.id)} disabled={actionId === dep.id} data-testid={`approve-deposit-${dep.id}`}><CheckCircle size={14} className="mr-1"/>Approve</Button>
                      <Button size="sm" variant="destructive" className="text-xs" onClick={() => setShowReject(dep.id)} data-testid={`reject-deposit-${dep.id}`}><XCircle size={14} className="mr-1"/>Reject</Button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}</div>}
      </CardContent>
    </Card>
  );
};

/* ─── TRANSACTIONS TAB ─── */
const TransactionsTab = ({ headers }) => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const fetchTx = useCallback(async () => { setLoading(true); try { setTransactions((await axios.get(`${API_URL}/api/admin/transactions?tx_type=${filter}&limit=200`, { headers })).data); } catch { toast.error('Failed'); } finally { setLoading(false); } }, [filter]);
  useEffect(() => { fetchTx(); }, [fetchTx]);

  const types = ['all','credit','contribution','lock','payout','security_hold','security_release','auto_debit','default_collateral'];
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Activity size={18} className="text-[#2272C5]"/>Transactions ({transactions.length})</CardTitle>
        <div className="flex flex-wrap gap-1.5 mt-2">{types.map(t => (
          <Button key={t} size="sm" variant={filter === t ? 'default' : 'outline'} className={`text-[10px] rounded-lg px-2 py-1 h-7 ${filter === t ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'}`} onClick={() => setFilter(t)} data-testid={`tx-filter-${t}`}>{t === 'all' ? 'All' : t.replace(/_/g,' ')}</Button>
        ))}</div>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-14 rounded-xl"/> :
        transactions.length === 0 ? <p className="text-center py-8 text-slate-500">No transactions</p> :
        <div className="space-y-2 max-h-[600px] overflow-y-auto">{transactions.map(tx => {
          const isCredit = ['credit','payout','security_release','qualification_release'].includes(tx.type);
          const txDesc = tx.description; const txUser = tx.user_name; const txDate = tx.created_at; const txAmt = tx.amount; const txType = tx.type;
          return (
            <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50" data-testid={`admin-tx-${tx.id}`}>
              <div className={`p-2 rounded-lg ${isCredit ? 'bg-blue-100' : 'bg-amber-100'}`}>{isCredit ? <ArrowDownLeft size={14} className="text-blue-600"/> : <ArrowUpRight size={14} className="text-amber-600"/>}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#0B1F3A] truncate">{txDesc}</p><p className="text-xs text-slate-500">{txUser} &middot; {new Date(txDate).toLocaleDateString('en-NG',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</p></div>
              <div className="text-right"><p className={`text-sm font-bold ${isCredit ? 'text-blue-600' : 'text-amber-600'}`}>{isCredit ? '+' : '-'}{'\u20a6'}{new Intl.NumberFormat('en-NG').format(txAmt)}</p><Badge className="text-[9px] bg-slate-100 text-slate-600">{txType.replace(/_/g,' ')}</Badge></div>
            </div>
          );
        })}</div>}
      </CardContent>
    </Card>
  );
};

/* ─── KYC TAB ─── */
const KYCTab = ({ headers }) => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [revokeNotes, setRevokeNotes] = useState('');
  const [showRevoke, setShowRevoke] = useState(null);
  const fetchKyc = useCallback(async () => { setLoading(true); try { setUsers((await axios.get(`${API_URL}/api/admin/kyc-submissions?status=${filter}`, { headers })).data); } catch { toast.error('Failed'); } finally { setLoading(false); } }, [filter]);
  useEffect(() => { fetchKyc(); }, [fetchKyc]);
  const approveKyc = async (uid) => { setActionId(uid); try { await axios.put(`${API_URL}/api/admin/kyc/${uid}/approve`, { admin_notes: 'Verified' }, { headers }); toast.success('Approved'); fetchKyc(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const revokeKyc = async (uid) => { if (!revokeNotes.trim()) { toast.error('Reason required'); return; } setActionId(uid); try { await axios.put(`${API_URL}/api/admin/kyc/${uid}/revoke`, { admin_notes: revokeNotes }, { headers }); toast.success('Revoked'); setShowRevoke(null); setRevokeNotes(''); fetchKyc(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><UserCheck size={18} className="text-[#2272C5]"/>KYC Panel</CardTitle>
        <div className="flex gap-2 mt-2">{['all','verified','pending','unverified'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} className={`text-xs rounded-lg ${filter === f ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'}`} onClick={() => setFilter(f)} data-testid={`kyc-filter-${f}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</Button>
        ))}</div>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-20 rounded-xl"/> :
        users.length === 0 ? <p className="text-center py-8 text-slate-500">No users</p> :
        <div className="space-y-3">{users.map(u => {
          const uVerified = u.is_verified;
          const uName = u.full_name;
          const uEmail = u.email;
          const uLevel = u.kyc_level;
          const uKycName = u.kyc_full_name;
          const uKycPhone = u.kyc_phone;
          const uPhoneVerified = u.phone_verified;
          const uHasBvn = u.has_bvn_nin;
          const uSubmitted = u.kyc_submitted_at;
          const uNotes = u.kyc_admin_notes;
          const bgClass = uVerified ? 'bg-emerald-50 border-emerald-200' : uSubmitted ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200';
          return (
            <div key={u.id} className={`p-4 rounded-xl border-2 ${bgClass}`} data-testid={`kyc-user-${u.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div><div className="flex items-center gap-2"><p className="font-bold text-[#0B1F3A]">{uName}</p>{uVerified && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Verified</Badge>}</div><p className="text-xs text-slate-500">{uEmail}</p></div>
                <Badge className={uLevel > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>Level {uLevel}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-2">
                <span>Name: <strong>{uKycName || '\u2014'}</strong></span><span>Phone: <strong>{uKycPhone || '\u2014'}</strong> {uPhoneVerified && <CheckCircle size={10} className="inline text-emerald-500"/>}</span>
                <span>BVN/NIN: <strong>{uHasBvn ? 'Submitted' : 'No'}</strong></span><span>Submitted: <strong>{uSubmitted ? new Date(uSubmitted).toLocaleDateString('en-NG') : '\u2014'}</strong></span>
              </div>
              {uNotes && <p className="text-xs text-slate-500 bg-white/60 p-2 rounded-lg mb-2">Note: {uNotes}</p>}
              <div className="flex gap-2 mt-2">
                {!uVerified && <Button size="sm" className="bg-emerald-600 text-white text-xs" onClick={() => approveKyc(u.id)} disabled={actionId === u.id} data-testid={`kyc-approve-${u.id}`}><CheckCircle size={12} className="mr-1"/>Approve</Button>}
                {uVerified && (showRevoke === u.id ? (
                  <div className="flex-1 space-y-2"><input value={revokeNotes} onChange={e => setRevokeNotes(e.target.value)} placeholder="Reason..." className="w-full px-3 py-2 rounded-lg border border-red-300 text-sm" data-testid={`kyc-revoke-reason-${u.id}`}/>
                  <div className="flex gap-2"><Button size="sm" variant="destructive" className="text-xs" onClick={() => revokeKyc(u.id)} disabled={actionId === u.id} data-testid={`kyc-confirm-revoke-${u.id}`}>Revoke</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowRevoke(null); setRevokeNotes(''); }}>Cancel</Button></div></div>
                ) : (<Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300" onClick={() => setShowRevoke(u.id)} data-testid={`kyc-revoke-${u.id}`}>Revoke</Button>))}
              </div>
            </div>
          );
        })}</div>}
      </CardContent>
    </Card>
  );
};

/* ─── USERS TAB ─── */
const UsersTab = ({ headers, fmtCur, onUpdate }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionId, setActionId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [freezeReason, setFreezeReason] = useState('');
  const [showFlag, setShowFlag] = useState(null);
  const [showFreeze, setShowFreeze] = useState(null);
  const [showScore, setShowScore] = useState(null);
  const [newScore, setNewScore] = useState('');
  const [scoreReason, setScoreReason] = useState('');
  const [showRename, setShowRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const fetchUsers = useCallback(async () => { setLoading(true); try { setUsers((await axios.get(`${API_URL}/api/admin/users`, { headers })).data); } catch { toast.error('Failed'); } finally { setLoading(false); } }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const doFlag = async (uid) => { if (!flagReason.trim()) { toast.error('Required'); return; } setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/flag`, { reason: flagReason }, { headers }); toast.success('Flagged'); setShowFlag(null); setFlagReason(''); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doUnflag = async (uid) => { setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/unflag`, {}, { headers }); toast.success('Unflagged'); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doFreeze = async (uid) => { if (!freezeReason.trim()) { toast.error('Required'); return; } setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/freeze`, { reason: freezeReason }, { headers }); toast.success('Frozen'); setShowFreeze(null); setFreezeReason(''); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doUnfreeze = async (uid) => { setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/unfreeze`, {}, { headers }); toast.success('Unfrozen'); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doScore = async (uid) => { const s = parseInt(newScore); if (isNaN(s) || s < 0 || s > 100) { toast.error('0-100'); return; } if (!scoreReason.trim()) { toast.error('Required'); return; } setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/adjust-score`, { score: s, reason: scoreReason }, { headers }); toast.success('Adjusted'); setShowScore(null); setNewScore(''); setScoreReason(''); fetchUsers(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doRename = async (uid) => { if (!newName.trim()) { toast.error('Name required'); return; } setActionId(uid); try { await axios.put(`${API_URL}/api/admin/users/${uid}/rename`, { new_name: newName.trim() }, { headers }); toast.success('Renamed'); setShowRename(null); setNewName(''); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };
  const doDelete = async (uid) => { setActionId(uid); try { await axios.delete(`${API_URL}/api/admin/users/${uid}`, { headers }); toast.success('User deleted'); setShowDelete(null); fetchUsers(); onUpdate(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setActionId(null); } };

  const filtered = users.filter(u => { const s = searchTerm.toLowerCase(); return !s || (u.full_name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s); });

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Users size={18} className="text-[#2272C5]"/>Users ({users.length})</CardTitle>
        <div className="relative mt-2"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="user-search-input"/></div>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-20 rounded-xl"/> :
        <div className="space-y-3 max-h-[600px] overflow-y-auto">{filtered.map(u => {
          const uScore = u.credibility_score != null ? u.credibility_score : 100;
          const riskLevel = uScore >= 80 ? 'low' : uScore >= 60 ? 'medium' : 'high';
          const riskColor = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };
          const avail = u.wallet ? u.wallet.available_balance : 0;
          const locked = u.wallet ? u.wallet.locked_balance : 0;
          const uFlagged = u.is_flagged;
          const uFrozen = u.is_frozen;
          const uAdmin = u.is_admin;
          const bgClass = uFrozen ? 'bg-red-50 border-red-300' : uFlagged ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200';
          return (
            <div key={u.id} className={`p-4 rounded-xl border-2 ${bgClass}`} data-testid={`admin-user-${u.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#0B1F3A] flex items-center justify-center"><span className="text-xs font-bold text-white">{(u.full_name || 'U').charAt(0).toUpperCase()}</span></div>
                  <div><p className="font-bold text-[#0B1F3A] text-sm">{u.full_name}</p><p className="text-[10px] text-slate-500">{u.email}</p></div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex gap-1">
                    {uAdmin && <Badge className="bg-purple-100 text-purple-700 text-[9px]">Admin</Badge>}
                    {u.is_verified && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">KYC</Badge>}
                    {uFlagged && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Flagged</Badge>}
                    {uFrozen && <Badge className="bg-red-100 text-red-700 text-[9px]">Frozen</Badge>}
                    <Badge className={`${riskColor[riskLevel]} text-[9px]`}>{uScore}</Badge>
                  </div>
                  <p className="text-xs font-medium text-[#2272C5]">{fmtCur(avail)}</p>
                  {locked > 0 && <p className="text-[10px] text-amber-600">{fmtCur(locked)} locked</p>}
                </div>
              </div>
              {(u.flag_reason || u.freeze_reason) && <div className="text-xs text-red-700 bg-red-100/60 p-2 rounded-lg mb-2">{u.flag_reason && <p>Flag: {u.flag_reason}</p>}{u.freeze_reason && <p>Freeze: {u.freeze_reason}</p>}</div>}
              {!uAdmin && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {uFlagged ? <Button size="sm" variant="outline" className="text-xs text-emerald-600" onClick={() => doUnflag(u.id)} disabled={actionId === u.id} data-testid={`unflag-user-${u.id}`}><CheckCircle size={12} className="mr-1"/>Unflag</Button>
                  : showFlag === u.id ? <div className="flex-1 space-y-2"><input value={flagReason} onChange={e => setFlagReason(e.target.value)} placeholder="Reason..." className="w-full px-3 py-1.5 rounded-lg border border-amber-300 text-xs" data-testid={`flag-reason-${u.id}`}/><div className="flex gap-2"><Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs" onClick={() => doFlag(u.id)} disabled={actionId === u.id} data-testid={`confirm-flag-${u.id}`}>Flag</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowFlag(null); setFlagReason(''); }}>Cancel</Button></div></div>
                  : <Button size="sm" variant="outline" className="text-xs text-amber-600" onClick={() => setShowFlag(u.id)} data-testid={`flag-user-${u.id}`}><Flag size={12} className="mr-1"/>Flag</Button>}

                  {uFrozen ? <Button size="sm" variant="outline" className="text-xs text-emerald-600" onClick={() => doUnfreeze(u.id)} disabled={actionId === u.id} data-testid={`unfreeze-user-${u.id}`}><CheckCircle size={12} className="mr-1"/>Unfreeze</Button>
                  : showFreeze === u.id ? <div className="flex-1 space-y-2"><input value={freezeReason} onChange={e => setFreezeReason(e.target.value)} placeholder="Reason..." className="w-full px-3 py-1.5 rounded-lg border border-red-300 text-xs" data-testid={`freeze-reason-${u.id}`}/><div className="flex gap-2"><Button size="sm" variant="destructive" className="text-xs" onClick={() => doFreeze(u.id)} disabled={actionId === u.id} data-testid={`confirm-freeze-${u.id}`}>Freeze</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowFreeze(null); setFreezeReason(''); }}>Cancel</Button></div></div>
                  : <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => setShowFreeze(u.id)} data-testid={`freeze-user-${u.id}`}><Ban size={12} className="mr-1"/>Freeze</Button>}

                  {showScore === u.id ? <div className="flex-1 space-y-2 border-t border-slate-200 pt-2 mt-1"><div className="flex gap-2"><input type="number" min="0" max="100" value={newScore} onChange={e => setNewScore(e.target.value)} placeholder="0-100" className="w-20 px-2 py-1.5 rounded-lg border border-blue-300 text-xs" data-testid={`score-input-${u.id}`}/><input value={scoreReason} onChange={e => setScoreReason(e.target.value)} placeholder="Reason..." className="flex-1 px-2 py-1.5 rounded-lg border border-blue-300 text-xs" data-testid={`score-reason-${u.id}`}/></div><div className="flex gap-2"><Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => doScore(u.id)} disabled={actionId === u.id} data-testid={`confirm-score-${u.id}`}>Adjust</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowScore(null); setNewScore(''); setScoreReason(''); }}>Cancel</Button></div></div>
                  : <Button size="sm" variant="outline" className="text-xs text-blue-600" onClick={() => setShowScore(u.id)} data-testid={`adjust-score-${u.id}`}><TrendingUp size={12} className="mr-1"/>Score</Button>}

                  {showRename === u.id ? <div className="flex-1 space-y-2 border-t border-slate-200 pt-2 mt-1"><div className="flex gap-2"><input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New name..." className="flex-1 px-2 py-1.5 rounded-lg border border-[#2272C5] text-xs" data-testid={`rename-input-${u.id}`}/></div><div className="flex gap-2"><Button size="sm" className="bg-[#2272C5] hover:bg-[#1a5ea0] text-white text-xs" onClick={() => doRename(u.id)} disabled={actionId === u.id} data-testid={`confirm-rename-${u.id}`}>Rename</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowRename(null); setNewName(''); }}>Cancel</Button></div></div>
                  : <Button size="sm" variant="outline" className="text-xs text-[#2272C5]" onClick={() => { setShowRename(u.id); setNewName(u.full_name || ''); }} data-testid={`rename-user-${u.id}`}><FileText size={12} className="mr-1"/>Rename</Button>}

                  {showDelete === u.id ? <div className="flex-1 space-y-2 border-t border-red-200 pt-2 mt-1"><p className="text-xs text-red-600 font-semibold">Delete this user and all their data? This cannot be undone.</p><div className="flex gap-2"><Button size="sm" variant="destructive" className="text-xs" onClick={() => doDelete(u.id)} disabled={actionId === u.id} data-testid={`confirm-delete-${u.id}`}>Delete Forever</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => setShowDelete(null)}>Cancel</Button></div></div>
                  : <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300" onClick={() => setShowDelete(u.id)} data-testid={`delete-user-${u.id}`}><XCircle size={12} className="mr-1"/>Delete</Button>}
                </div>
              )}
            </div>
          );
        })}</div>}
      </CardContent>
    </Card>
  );
};

/* ─── SCHEDULER TAB ─── */
const SchedulerTab = ({ headers, onUpdate }) => {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState({});
  const [defaultsResult, setDefaultsResult] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/scheduler/status`, { headers }).then(r => setStatus(r.data)).catch(() => {}); }, []);
  const run = async (ep, key, msg) => { setRunning(p => ({...p, [key]: true})); try { const r = await axios.post(`${API_URL}/api/scheduler/${ep}`, {}, { headers }); toast.success(msg(r.data)); if (key === 'defaults') setDefaultsResult(r.data); onUpdate(); } catch { toast.error('Failed'); } finally { setRunning(p => ({...p, [key]: false})); } };

  const ajoToday = status ? status.pending_ajo_contributions_today : 0;
  const savToday = status ? status.pending_savings_contributions_today : 0;
  const ajoTmrw = status ? status.pending_ajo_contributions_tomorrow : 0;
  const savTmrw = status ? status.pending_savings_contributions_tomorrow : 0;
  const totalToday = status ? status.total_pending_today : 0;

  return (
    <Card className="glass-card border-2 border-[#0B1F3A]/20">
      <CardHeader className="pb-2"><CardTitle className="text-xl font-bold text-[#0B1F3A] flex items-center gap-2"><Clock size={22}/>Scheduler</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-xl bg-slate-100 border-2 border-slate-200">
          <h4 className="font-bold text-[#0B1F3A] mb-3">Status</h4>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 rounded-lg bg-white border-2 border-emerald-200"><p className="text-sm text-slate-600">Ajo Today</p><p className="text-2xl font-bold text-emerald-600">{ajoToday}</p></div>
            <div className="p-3 rounded-lg bg-white border-2 border-emerald-200"><p className="text-sm text-slate-600">Savings Today</p><p className="text-2xl font-bold text-emerald-600">{savToday}</p></div>
            <div className="p-3 rounded-lg bg-white border-2 border-amber-200"><p className="text-sm text-slate-600">Ajo Tomorrow</p><p className="text-2xl font-bold text-amber-600">{ajoTmrw}</p></div>
            <div className="p-3 rounded-lg bg-white border-2 border-amber-200"><p className="text-sm text-slate-600">Savings Tomorrow</p><p className="text-2xl font-bold text-amber-600">{savTmrw}</p></div>
          </div>
          <div className="p-3 rounded-lg bg-[#0B1F3A] text-white"><p className="font-bold">Total Due Today: {totalToday}</p></div>
        </div>
        <AutoRunHistory headers={headers} />
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200"><div className="flex items-center gap-2 mb-2"><Bell size={20} className="text-amber-600"/><h4 className="font-bold text-amber-800">Reminders</h4></div><p className="text-sm text-amber-700 mb-3">Send to users due tomorrow</p><Button onClick={() => run('send-reminders', 'reminders', d => `${d.reminders_sent} sent`)} disabled={running.reminders} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="run-reminders-btn">{running.reminders ? 'Sending...' : 'Send Reminders'}</Button></div>
          <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-200"><div className="flex items-center gap-2 mb-2"><CreditCard size={20} className="text-emerald-600"/><h4 className="font-bold text-emerald-800">Auto-Debits</h4></div><p className="text-sm text-emerald-700 mb-3">Process deductions for today</p><Button onClick={() => run('process-auto-debits', 'debits', d => `${d.processed?.successful || 0} ok, ${d.processed?.failed_with_penalty || 0} penalties`)} disabled={running.debits} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="run-debits-btn">{running.debits ? '...' : 'Run Auto-Debits'}</Button></div>
        </div>
        <div className="p-4 rounded-xl bg-red-50 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle size={22} className="text-red-600"/><h4 className="font-bold text-red-800 text-lg">Default Protection</h4></div>
          {defaultsResult && <div className="p-3 rounded-lg bg-red-100 border border-red-300 mb-4"><p className="text-sm text-red-800 font-bold">Results:</p><div className="grid grid-cols-2 gap-2 mt-2 text-sm text-red-700"><span>Groups: <strong>{defaultsResult.groups_checked}</strong></span><span>Members: <strong>{defaultsResult.members_checked}</strong></span><span>Defaults: <strong>{defaultsResult.defaults_found}</strong></span><span>Collateral: <strong>{'\u20a6'}{new Intl.NumberFormat('en-NG').format(defaultsResult.collateral_applied || 0)}</strong></span></div></div>}
          <Button onClick={() => run('check-defaults', 'defaults', d => `${d.defaults_found} defaults`)} disabled={running.defaults} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="check-defaults-btn">{running.defaults ? '...' : 'Check Defaults'}</Button>
        </div>
        <div className="p-4 rounded-xl bg-[#2272C5]/10 border-2 border-[#2272C5]/30">
          <div className="flex items-center gap-2 mb-2"><Zap size={20} className="text-[#2272C5]"/><h4 className="font-bold text-[#0B1F3A]">Weekly Reward Summaries</h4></div>
          <p className="text-sm text-slate-600 mb-3">Generate weekly reward summaries for all users. Sends notifications and emails.</p>
          <Button onClick={() => run('generate-weekly-summaries', 'weeklySummary', d => `${d.generated} summaries generated`)} disabled={running.weeklySummary} className="w-full bg-[#2272C5] hover:bg-[#1a5fa3] text-white font-bold" data-testid="generate-summaries-btn">{running.weeklySummary ? 'Generating...' : 'Generate Weekly Summaries'}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;

/* ─── ADMIN REWARDS TAB ─── */
const AdminRewardsTab = ({ headers }) => {
  const [rewardStats, setRewardStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/rewards/admin/stats`, { headers })
      .then((r) => setRewardStats(r.data))
      .catch(() => toast.error('Failed to load reward stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-48 rounded-2xl" />;
  if (!rewardStats) return <p className="text-sm text-slate-500">No reward data</p>;

  const dist = rewardStats.circle_distribution || {};

  return (
    <div className="space-y-4" data-testid="admin-rewards-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="glass-card"><CardContent className="p-4"><p className="text-2xl font-bold text-[#0B1F3A]">{rewardStats.total_users_with_rewards}</p><p className="text-xs text-slate-500">Users Tracked</p></CardContent></Card>
        <Card className="glass-card"><CardContent className="p-4"><p className="text-2xl font-bold text-emerald-600">{rewardStats.avg_credibility_score || 0}</p><p className="text-xs text-slate-500">Avg Credibility</p></CardContent></Card>
        <Card className="glass-card"><CardContent className="p-4"><p className="text-2xl font-bold text-[#2272C5]">{rewardStats.today_transactions}</p><p className="text-xs text-slate-500">Today Activity</p></CardContent></Card>
        <Card className="glass-card"><CardContent className="p-4"><p className="text-2xl font-bold text-amber-600">{rewardStats.daily_capped_users || 0}</p><p className="text-xs text-slate-500">Capped Today</p></CardContent></Card>
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base text-[#0B1F3A]">Circle Distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(dist).map(([name, count]) => {
              const circle = (rewardStats.circles || []).find(c => c.name === name);
              const total = rewardStats.total_users_with_rewards || 1;
              const pct = Math.round((count / total) * 100) || 0;
              return (
                <div key={name} className="flex items-center gap-3" data-testid={`dist-${name.toLowerCase()}`}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: circle?.color || '#ccc' }} />
                  <span className="text-sm font-medium text-slate-700 w-20">{name}</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: circle?.color || '#ccc' }} />
                  </div>
                  <span className="text-xs font-bold text-slate-600 w-12 text-right">{count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
